import { useEffect, useRef, useState } from "react";
import { FolderOpen, Upload, Download, Trash2, FileText, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PortalHeader, EmptyState, fmtDate, fmtBytes } from "@/pages/client/_shared";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function ClientDocuments() {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/portal/documents").then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post("/portal/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded"); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const download = async (doc) => {
    try {
      const { data } = await api.get(`/portal/documents/${doc.doc_id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a"); a.href = url; a.download = doc.filename;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not download"); }
  };

  const remove = async (id) => {
    setDocs((d) => d.filter((x) => x.doc_id !== id));
    try { await api.delete(`/portal/documents/${id}`); toast.success("Document deleted"); } catch { load(); }
  };

  return (
    <div className="fv-fade-up">
      <PortalHeader title="Documents" subtitle="Securely upload and download files shared with your advisor."
        action={
          <>
            <input ref={fileRef} type="file" onChange={upload} className="hidden" data-testid="client-doc-file-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="client-doc-upload-btn"
              className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full shrink-0">
              <Upload className="w-4 h-4 mr-1" /> {uploading ? "Uploading…" : "Upload"}</Button>
          </>
        } />

      {docs.length === 0 ? (
        <EmptyState icon={FolderOpen} text="No documents yet. Upload a file or wait for your advisor to share one." testid="client-docs-empty" />
      ) : (
        <div className="space-y-2.5" data-testid="client-docs-list">
          {docs.map((d) => (
            <div key={d.doc_id} className="flex items-center justify-between rounded-xl border border-white/8 bg-[#121214] p-4">
              <div className="flex items-center gap-3 min-w-0">
                <FileText className="w-5 h-5 text-[#D4AF37] shrink-0" />
                <div className="min-w-0"><div className="text-white text-sm font-medium truncate">{d.filename}</div>
                  <div className="text-xs text-[#71717A] flex items-center gap-2">
                    {fmtBytes(d.size)} · {fmtDate(d.created_at)}
                    {d.uploaded_by === "admin" && <span className="inline-flex items-center gap-1 text-[#D4AF37]"><ShieldCheck className="w-3 h-3" /> From advisor</span>}
                  </div></div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => download(d)} data-testid={`client-doc-download-${d.doc_id}`} className="text-[#A1A1AA] hover:text-white"><Download className="w-4 h-4" /></button>
                {d.uploaded_by === "client" && (
                  <button onClick={() => remove(d.doc_id)} data-testid={`client-doc-delete-${d.doc_id}`} className="text-[#71717A] hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
